import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { approveDailyExpense, rejectDailyExpense } from "../../services/dailyExpense";
import { pushGlobalSuccess } from "../../success/globalSuccess";
import { formatNumber, numberWithCommas } from "../../utils";
import DailyExpenseStatusChip from "./DailyExpenseStatusChip";
import RejectReasonPanel from "./RejectReasonPanel";
import ReviewDayContent from "./ReviewDayContent";
import SkeletonCard from "./SkeletonCard";
import StateCard from "./StateCard";
import { EMPTY_CELL_TEXT } from "./constants";
import { chipKindOf } from "./dailyExpenseStatus";
import { formatBusinessDate, formatDayTitle, formatTimestamp } from "./format";
import { useCategoryLayout, useDailyExpenseDay, useDayKey } from "./useDailyExpenseData";

/** Wide enough for the ฝ่ายขาย table's five amounts beside its pinned สาย and รวม. */
const DRAWER_WIDTH = 880;

/** The branch-day head office opened, from a list row or a cell of the branch × day view. */
export interface ReviewTarget {
  branchCode: string;
  businessDate: string;
}

type ReviewWrite = "APPROVE" | "REJECT";

interface ReviewDrawerProps {
  target: ReviewTarget | null;
  onClose: () => void;
  /** Fired after an approve or a reject, so the list reads the new status. */
  onChanged: () => void;
  /**
   * Fired after a refused approve or reject (status moved, month closed meanwhile …): the drawer
   * reads the day again, and the list should too, so no button stays live on a stale status.
   */
  onStale: () => void;
}

/**
 * S3 drawer: the day's document read-only, with อนุมัติ / ตีกลับ as the server's `actions` allow —
 * an approved document can still be sent back until its month is closed. ตีกลับ opens a reason
 * box first; the reason is required.
 */
export default function ReviewDrawer({
  target,
  onClose,
  onChanged,
  onStale,
}: Readonly<ReviewDrawerProps>) {
  const categories = useCategoryLayout();
  const dayAnswer = useDailyExpenseDay(useDayKey(target?.businessDate ?? null, target?.branchCode));
  const day = dayAnswer.data;
  const layout = categories.data;
  const [isRejecting, setRejecting] = useState(false);
  const [pendingWrite, setPendingWrite] = useState<ReviewWrite | null>(null);

  useEffect(() => setRejecting(false), [target]);

  const document = day?.document ?? null;
  const canApprove = Boolean(day?.actions.can_approve && document);
  const canReject = Boolean(day?.actions.can_reject && document);
  const subject = day ? `${day.branch_code} · ${formatBusinessDate(day.business_date)}` : "";

  const retry = () => {
    if (dayAnswer.isFailed) dayAnswer.reload();
    if (categories.isFailed) categories.reload();
  };
  const startReject = () => setRejecting(true);
  const cancelReject = () => setRejecting(false);
  const refreshAfterRefusal = () => {
    setRejecting(false);
    dayAnswer.reload();
    onStale();
  };

  const approve = async () => {
    if (!document || !day) return;
    setPendingWrite("APPROVE");
    try {
      await approveDailyExpense(document.uuid);
      pushGlobalSuccess(`อนุมัติแล้ว · ${subject} · รวม ${formatNumber(day.totals.total)} บาท`);
      onChanged();
    } catch {
      // The interceptor has already shown the server's message (e.g. the status moved meanwhile).
      refreshAfterRefusal();
    } finally {
      setPendingWrite(null);
    }
  };
  const reject = async (reason: string) => {
    if (!document) return;
    setPendingWrite("REJECT");
    try {
      await rejectDailyExpense(document.uuid, reason);
      pushGlobalSuccess(`ตีกลับแล้ว · ${subject} ส่งกลับให้สาขาแก้ไข`);
      onChanged();
    } catch {
      // The interceptor has already shown the server's message.
      refreshAfterRefusal();
    } finally {
      setPendingWrite(null);
    }
  };

  const actionHint =
    document?.status === "APPROVED"
      ? "อนุมัติแล้ว ยังตีกลับได้จนกว่าจะปิดเดือน"
      : "ตรวจยอดกับแจ้งยอดโอนก่อนอนุมัติ";

  return (
    <Drawer
      anchor="right"
      open={target !== null}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: "100%", md: DRAWER_WIDTH }, p: 0 } } }}
    >
      <Stack sx={{ height: "100%" }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          gap={1.5}
          sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: "divider" }}
        >
          <Stack gap={0.5}>
            <Stack direction="row" alignItems="center" gap={1.25}>
              <Typography variant="h6" fontWeight={600}>
                สาขา {target?.branchCode}
              </Typography>
              {day && (
                <DailyExpenseStatusChip kind={chipKindOf(document?.status ?? null, day.is_locked)} />
              )}
            </Stack>
            {day && (
              <Typography variant="body2" color="text.secondary">
                {formatDayTitle(day.business_date)} · {numberWithCommas(day.routes.length)} สาย ·
                ส่งโดย {document?.submitted_by_name ?? EMPTY_CELL_TEXT}{" "}
                {formatTimestamp(document?.submitted_at ?? null)}
              </Typography>
            )}
          </Stack>
          <IconButton onClick={onClose} aria-label="ปิด">
            <CloseIcon />
          </IconButton>
        </Stack>

        <Box sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 2 }}>
          {(dayAnswer.isFailed || categories.isFailed) && (
            <StateCard
              title="โหลดเอกสารค่าใช้จ่ายไม่สำเร็จ"
              text="เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ กรุณาลองใหม่อีกครั้ง"
              onRetry={retry}
            />
          )}
          {(dayAnswer.isLoading || categories.isLoading) && <SkeletonCard />}
          {day && layout && <ReviewDayContent day={day} layout={layout} />}
        </Box>

        {(canApprove || canReject) && (
          <Box sx={{ px: 2.5, py: 2, borderTop: 1, borderColor: "divider" }}>
            {isRejecting ? (
              <RejectReasonPanel
                isSending={pendingWrite === "REJECT"}
                onCancel={cancelReject}
                onConfirm={reject}
              />
            ) : (
              <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1.5}>
                <Typography variant="body2" color="text.secondary">
                  {actionHint}
                </Typography>
                <Stack direction="row" gap={1}>
                  {canReject && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={startReject}
                      disabled={pendingWrite !== null}
                    >
                      ตีกลับ
                    </Button>
                  )}
                  {canApprove && (
                    <Button
                      variant="contained"
                      onClick={approve}
                      loading={pendingWrite === "APPROVE"}
                    >
                      อนุมัติ
                    </Button>
                  )}
                </Stack>
              </Stack>
            )}
          </Box>
        )}
      </Stack>
    </Drawer>
  );
}
